import React from 'react';
import '../../styles/common.css';

interface NumericLabelProps {
  value: number;
  mode: 'rating' | 'violations';
  warningThreshold?: number;
  dangerThreshold?: number;
  className?: string;
  showZero?: boolean;
  tooltip?: string;
}

const NumericLabel: React.FC<NumericLabelProps> = ({
  value,
  mode,
  warningThreshold,
  dangerThreshold,
  className = '',
  showZero = true,
  tooltip
}) => {
  // Don't render if value is 0 and showZero is false
  if (value === 0 && !showZero) {
    return null;
  }

  // Set default thresholds based on mode
  const getDefaultThresholds = () => {
    if (mode === 'rating') {
      return {
        warning: warningThreshold ?? 80,
        danger: dangerThreshold ?? 60
      };
    } else {
      return {
        warning: warningThreshold ?? 1,
        danger: dangerThreshold ?? 3
      };
    }
  };

  const { warning, danger } = getDefaultThresholds();

  // Generate tooltip text if not provided
  const generateTooltip = (): string => {
    if (tooltip) return tooltip;
    
    if (mode === 'rating') {
      return `Score: ${value} | Thresholds: Good ≥${warning}, Warning ${danger}-${warning-1}, Poor <${danger}`;
    } else {
      return `Violations: ${value} | Thresholds: Good <${warning}, Warning ${warning}-${danger-1}, Poor ≥${danger}`;
    }
  };

  // Determine the appropriate label variant based on mode and thresholds
  const getLabelVariant = (): string => {
    if (mode === 'rating') {
      // Rating mode: Higher is better
      if (value >= warning) return 'numeric-label-success';
      if (value >= danger) return 'numeric-label-warning';
      return 'numeric-label-danger';
    } else {
      // Violations mode: Higher is worse
      if (value >= danger) return 'numeric-label-danger';
      if (value >= warning) return 'numeric-label-warning';
      return 'numeric-label-success';
    }
  };

  return (
    <span 
      className={`numeric-label ${getLabelVariant()} ${className}`}
      title={generateTooltip()}
    >
      {value}
    </span>
  );
};

export default NumericLabel; 